import React, { useEffect, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Segmented } from "antd";
import * as echarts from "echarts/core";
import { LineChart } from "echarts/charts";
import { GridComponent, TooltipComponent, LegendComponent, MarkAreaComponent } from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";
import { useEventBus } from "shared/services/eventBus";
import { ROBOT_EVENTS, getRobotEvent } from "shared/services/eventBus/eventTypes";
import { useConnectionManager } from "features/robot-teleop/contexts/RobotConnectionManagerContext";
import { PEER_INTERNAL_EVENTS, PEER_STATES } from "features/robot-teleop/webrtc/peerStates";

echarts.use([LineChart, GridComponent, TooltipComponent, LegendComponent, MarkAreaComponent, CanvasRenderer]);

// Categorical palette (dark-surface steps), one fixed slot per peer in peer
// order — identity never follows rank, so a peer keeps its colour when others
// disconnect. Status colours are never used for series.
const SERIES_COLORS = ["#3987e5", "#d95926", "#199e70", "#c98500", "#d55181", "#008300", "#9085e9", "#e66767"];

const METRICS = {
  jitter: { label: "Jitter", unit: "ms", warn: 30, pick: (s) => s?.video?.jitterMs ?? s?.audio?.jitterMs ?? null, max: null },
  rtt: { label: "RTT", unit: "ms", warn: 150, pick: (s) => s?.rttMs ?? null, max: null },
  fps: { label: "FPS", unit: "fps", warn: null, pick: (s) => s?.video?.fps ?? null, max: null },
};

const CHROME = {
  ink2: "#c3c2b7",
  ink3: "#898781",
  grid: "#2c2c2a",
  axis: "#383835",
  surface: "#1a1a19",
};

const baseOption = (metricKey) => {
  const m = METRICS[metricKey];
  return {
    animation: false,
    backgroundColor: "transparent",
    grid: { left: 40, right: 8, top: 20, bottom: 18 },
    legend: {
      type: "plain",
      top: 0,
      right: 0,
      icon: "rect",
      itemWidth: 10,
      itemHeight: 3,
      itemGap: 10,
      textStyle: { color: CHROME.ink2, fontSize: 10 },
    },
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "line", lineStyle: { color: CHROME.ink3, width: 1 } },
      backgroundColor: "rgba(26,26,25,0.95)",
      borderColor: CHROME.axis,
      borderWidth: 1,
      textStyle: { color: "#ffffff", fontSize: 11 },
      valueFormatter: (v) => (v == null ? "–" : `${Number(v).toFixed(m.unit === "ms" ? 0 : 1)} ${m.unit}`),
    },
    xAxis: {
      type: "time",
      axisLine: { lineStyle: { color: CHROME.axis } },
      axisTick: { show: false },
      axisLabel: { color: CHROME.ink3, fontSize: 10, hideOverlap: true },
      splitLine: { show: false },
    },
    yAxis: {
      type: "value",
      min: 0,
      name: m.unit,
      nameTextStyle: { color: CHROME.ink3, fontSize: 10, align: "right" },
      nameGap: 6,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { color: CHROME.ink3, fontSize: 10 },
      splitLine: { lineStyle: { color: CHROME.grid, type: "solid", width: 1 } },
    },
    series: [],
  };
};

/**
 * PeerLinkChart — one metric (jitter / RTT / fps) over the last `windowMs` for
 * every peer of the robot, one line per peer. Samples are pushed straight into
 * the ECharts instance (coalesced per animation frame), so a stats tick never
 * re-renders React. Peers that are not connected show a gap.
 */
const PeerLinkChart = ({ windowMs = 120000 }) => {
  const { t } = useTranslation();
  const { manager, peerIds } = useConnectionManager();
  const eventBus = useEventBus();
  const [metric, setMetric] = useState("jitter");
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const buffersRef = useRef(new Map()); // peerId -> [{ t, sample|null }]
  const rafRef = useRef(null);
  const metricRef = useRef(metric);
  metricRef.current = metric;

  const flush = useCallback(() => {
    rafRef.current = null;
    const chart = chartRef.current;
    if (!chart) return;
    const m = METRICS[metricRef.current];
    const now = Date.now();
    const series = peerIds.map((id, i) => {
      const buf = buffersRef.current.get(id) || [];
      // prune
      while (buf.length && buf[0].t < now - windowMs) buf.shift();
      const data = buf.map((p) => [p.t, p.sample ? m.pick(p.sample) : null]);
      const s = {
        id,
        name: id,
        type: "line",
        showSymbol: false,
        connectNulls: false,
        smooth: false,
        lineStyle: { width: 2, cap: "round", join: "round" },
        color: SERIES_COLORS[i % SERIES_COLORS.length],
        emphasis: { focus: "series" },
        data,
      };
      if (i === 0 && m.warn != null) {
        s.markArea = {
          silent: true,
          itemStyle: { color: "rgba(250,178,25,0.08)" },
          data: [[{ yAxis: m.warn }, { yAxis: Number.MAX_SAFE_INTEGER }]],
        };
      }
      return s;
    });
    chart.setOption(
      { ...baseOption(metricRef.current), xAxis: { ...baseOption(metricRef.current).xAxis, min: now - windowMs, max: now }, series },
      { notMerge: true, lazyUpdate: true }
    );
  }, [peerIds, windowMs]);

  const scheduleFlush = useCallback(() => {
    if (rafRef.current != null) return;
    rafRef.current = window.requestAnimationFrame(flush);
  }, [flush]);

  // chart instance lifecycle
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const chart = echarts.init(el, null, { renderer: "canvas" });
    chartRef.current = chart;
    chart.setOption(baseOption(metricRef.current));
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(() => chart.resize()) : null;
    if (ro) ro.observe(el);
    return () => {
      if (ro) ro.disconnect();
      if (rafRef.current != null) window.cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      chart.dispose();
      chartRef.current = null;
    };
  }, []);

  // metric switch → redraw from buffers
  useEffect(() => { scheduleFlush(); }, [metric, scheduleFlush]);

  // subscriptions per peer
  useEffect(() => {
    if (!manager || peerIds.length === 0) return undefined;
    const subs = [];
    peerIds.forEach((peerId) => {
      const peer = manager.getPeer(peerId);
      if (!peer) return;
      peer.enableStats();
      const buf = buffersRef.current.get(peerId) || [];
      if (buf.length === 0) {
        peer.statsHistory.forEach((s) => buf.push({ t: s.timestamp, sample: s }));
      }
      buffersRef.current.set(peerId, buf);

      subs.push(eventBus.subscribe(getRobotEvent(peerId, PEER_INTERNAL_EVENTS.STATS_UPDATED), (payload) => {
        if (!payload?.sample) return;
        buf.push({ t: payload.sample.timestamp || Date.now(), sample: payload.sample });
        scheduleFlush();
      }));
      subs.push(eventBus.subscribe(getRobotEvent(peerId, ROBOT_EVENTS.CONNECTION_STATE), ({ state }) => {
        if (state !== PEER_STATES.CONNECTED) {
          buf.push({ t: Date.now(), sample: null }); // gap
          scheduleFlush();
        }
      }));
    });
    scheduleFlush();
    // keep the window sliding even when nobody reports
    const ticker = setInterval(scheduleFlush, 2000);
    return () => {
      clearInterval(ticker);
      subs.forEach((unsub) => { if (typeof unsub === "function") unsub(); });
      peerIds.forEach((peerId) => manager.getPeer(peerId)?.disableStats());
    };
  }, [manager, peerIds, eventBus, scheduleFlush]);

  return (
    <div className="peer-link-panel">
      <div className="panel-header">
        <span className="title">
          {t("teleop.chart.title")}{" "}
          <span style={{ fontWeight: 400 }}>{t("teleop.chart.window", { minutes: Math.round(windowMs / 60000) })}</span>
        </span>
        <Segmented
          size="small"
          value={metric}
          onChange={(v) => setMetric(v)}
          options={Object.keys(METRICS).map((key) => ({ value: key, label: t(`teleop.chart.${key}`) }))}
        />
      </div>
      {/* The chart container is always mounted: the ECharts instance is
          created once on mount, and the peer list is usually still empty then. */}
      <div className="panel-chart" ref={containerRef} style={peerIds.length === 0 ? { display: "none" } : undefined} />
      {peerIds.length === 0 && <div className="panel-empty">{t("teleop.chart.noPeers")}</div>}
    </div>
  );
};

export default React.memo(PeerLinkChart);
